import { ApprovedProofItem, publicApprovedProofItems } from "@/constants/approvedProof";

type ApprovedProofSectionProps = {
  items?: ApprovedProofItem[];
  eyebrow?: string;
  title?: string;
  description?: string;
  compact?: boolean;
};

const sourceLabels: Record<ApprovedProofItem["source"], string> = {
  demo: "Exemple démo",
  customer_consented: "Client approuvé",
};

const ApprovedProofSection = ({
  items = publicApprovedProofItems,
  eyebrow = "Exemples approuvés",
  title = "Des preuves visuelles publiables, sans données privées.",
  description = "Cette sélection utilise uniquement des visuels de démonstration ou des contenus client explicitement consentis. Aucun identifiant de commande, email, nom complet, adresse ou URL de stockage privée n'est exposé.",
  compact = false,
}: ApprovedProofSectionProps) => {
  const visibleItems = items.filter((item) => item.source === "demo" || Boolean(item.consentReference));

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <section className={compact ? "my-12" : "section-shell relative py-20"} aria-labelledby="approved-proof-title">
      <div className={compact ? undefined : "container relative mx-auto px-6"}>
        <div className="mb-8 max-w-3xl">
          <p className="eyebrow mb-3">{eyebrow}</p>
          <h2 id="approved-proof-title" className={compact ? "text-2xl font-semibold text-foreground" : "section-title text-foreground"}>
            {title}
          </h2>
          <p className="mt-4 text-base leading-7 text-foreground/65 md:text-lg md:leading-8">
            {description}
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {visibleItems.map((item) => (
            <article
              key={item.id}
              className="overflow-hidden rounded-[1.8rem] border border-white/65 bg-white/72 shadow-card backdrop-blur-md"
            >
              <div className="relative overflow-hidden bg-[#f7efe8]">
                <img
                  src={item.imageSrc}
                  alt={item.imageAlt}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-premium hover:scale-[1.03]"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/86 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-primary shadow-card backdrop-blur-sm">
                  {sourceLabels[item.source]}
                </span>
              </div>
              <div className="p-5">
                <div className="mb-3 flex flex-wrap gap-2 text-xs font-semibold text-primary">
                  <span className="rounded-full bg-primary/8 px-2.5 py-1">{item.styleLabel}</span>
                  <span className="rounded-full bg-primary/8 px-2.5 py-1">{item.scenario}</span>
                </div>
                <h3 className="text-lg font-semibold leading-6 text-foreground">{item.title}</h3>
                {item.quote && <p className="mt-3 text-sm leading-6 text-foreground/62">“{item.quote}”</p>}
                <p className="mt-4 text-xs font-medium text-foreground/45">
                  Affichage : {item.displayName}. Données privées exclues par design.
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ApprovedProofSection;
